import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { t } from '../lib/i18n';
import { messageFor } from '../lib/errors';
import { formatDate, formatMoney } from '../ui/format';
import { EmptyState, ErrorState, Loading } from '../ui/Page';
import { DataTable, type Column } from '../ui/DataTable';
import { Person } from '../ui/Avatar';
import { Menu } from '../ui/Menu';
import { useCreateIntent } from '../app/create-intent';
import { MemberSelect, useMembers } from '../ui/UserSelect';
import { listCatalogue, listPipelines, type Pipeline } from '../settings/catalogues-api';
import {
  ClockGlyph,
  EmptyTrayGlyph,
  FilterGlyph,
  ForecastIcon,
  GripGlyph,
  KanbanGlyph,
  ListGlyph,
  OpenValueIcon,
  SearchGlyph,
  StackIcon,
  WonBadgeGlyph,
  WonIcon,
} from './BoardIcons';
import {
  createDeal,
  deleteDeal,
  loadBoard,
  moveDeal,
  searchEntity,
  type BoardCard,
  type BoardColumn,
  type CompanyRow,
  type FilterGroup,
} from './api';

/**
 * The deals board (context.md §35, §134).
 *
 * Totals come from the server per stage and each column carries only its first page of cards, so a
 * funnel with thousands of deals still opens. What a column's header shows is therefore the whole
 * stage — its money and its count — and the cards under it are the first of them, not a sample that
 * adds up to something different.
 *
 * Filtering happens on the server for the same reason. Searching a title or narrowing to one
 * person's deals changes the totals too, which a filter applied in the browser could not do.
 *
 * **The card carries what somebody scanning the board needs and nothing else**: what it is, for
 * whom, how much, where it came from, who owns it and how long it has been sitting there. Everything
 * that changes the deal lives behind the card's own menu, because a board with three buttons on
 * every card is a board where the buttons are most of what you see — and the thing people actually
 * do, moving a deal along, is a drag rather than a button anyway.
 */
export function BoardPage(): React.JSX.Element {
  const client = useQueryClient();
  const navigate = useNavigate();

  const [pipelineId, setPipelineId] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [applied, setApplied] = useState('');
  const [ownerId, setOwnerId] = useState('');
  const [sourceId, setSourceId] = useState('');
  const [view, setView] = useState<'KANBAN' | 'LIST'>('KANBAN');
  /*
   * The card being dragged, and the column it came from.
   *
   * The card alone is not enough: dropping it back where it started would otherwise fire a move to
   * the stage it is already in — a write, an audit entry and a refetch for a gesture that changed
   * nothing. `BoardCard` carries no stage of its own, because the column it is in *is* its stage.
   */
  const [dragging, setDragging] = useState<{ card: BoardCard; fromStageId: string } | null>(null);
  const [over, setOver] = useState<string | null>(null);
  const [adding, setAdding] = useState<string | null>(null);
  const [title, setTitle] = useState('');
  const [error, setError] = useState<string | null>(null);

  const pipelines = useQuery({ queryKey: ['pipelines'], queryFn: listPipelines });
  const { options: people } = useMembers();

  const sources = useQuery({
    queryKey: ['catalogue', 'customer-sources'],
    queryFn: () => listCatalogue('customer-sources'),
  });

  const companies = useQuery({
    queryKey: ['companies', 'board-names'],
    queryFn: () => searchEntity<CompanyRow>('companies', { limit: 200 }),
  });

  const chosen =
    pipelineId ??
    pipelines.data?.pipelines.find((pipeline) => pipeline.is_default)?.id ??
    pipelines.data?.pipelines[0]?.id ??
    null;

  const filter = useMemo<FilterGroup | undefined>(() => {
    const conditions = [
      ...(applied.trim() ? [{ field: 'title', operator: 'contains', value: applied.trim() }] : []),
      ...(ownerId ? [{ field: 'owner_id', operator: 'eq', value: ownerId }] : []),
      ...(sourceId ? [{ field: 'source_id', operator: 'eq', value: sourceId }] : []),
    ];

    return conditions.length > 0
      ? ({ combinator: 'AND', conditions } as unknown as FilterGroup)
      : undefined;
  }, [applied, ownerId, sourceId]);

  const board = useQuery({
    queryKey: ['board', chosen, applied, ownerId, sourceId],
    queryFn: () =>
      loadBoard({ ...(chosen ? { pipelineId: chosen } : {}), ...(filter ? { filter } : {}) }),
    enabled: pipelines.isSuccess,
  });

  const refresh = (): void => void client.invalidateQueries({ queryKey: ['board'] });

  const move = useMutation({
    mutationFn: (input: { card: BoardCard; stageId: string }) =>
      moveDeal(input.card.id, { stageId: input.stageId, expectedVersion: input.card.version }),
    onSuccess: () => {
      setError(null);
      refresh();
    },
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  const remove = useMutation({
    mutationFn: (card: BoardCard) => deleteDeal(card.id),
    onSuccess: refresh,
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  const create = useMutation({
    mutationFn: (stageId: string) =>
      createDeal({ title: title.trim(), pipelineId: chosen!, stageId }),
    onSuccess: () => {
      setTitle('');
      setAdding(null);
      setError(null);
      refresh();
    },
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  /**
   * What a funnel is read for.
   *
   * The weighted forecast is each open stage's money times that stage's own likelihood — the number
   * the pipeline exists to produce. Won and open are counted separately because a board that adds
   * them together says nothing about either.
   */
  const totals = useMemo(() => {
    const columns = board.data?.columns ?? [];
    const open = columns.filter((column) => !column.stage.is_won && !column.stage.is_lost);

    return {
      forecast: open.reduce(
        (sum, column) => sum + (column.amount_cents * column.stage.probability) / 100,
        0,
      ),
      openAmount: open.reduce((sum, column) => sum + column.amount_cents, 0),
      openCount: open.reduce((sum, column) => sum + column.count, 0),
      openStages: open.length,
      won: columns
        .filter((column) => column.stage.is_won)
        .reduce((sum, column) => sum + column.amount_cents, 0),
      wonCount: columns
        .filter((column) => column.stage.is_won)
        .reduce((sum, column) => sum + column.count, 0),
    };
  }, [board.data]);

  const columns = board.data?.columns ?? [];
  const stages = columns.map((column) => column.stage);

  const companyName = (id: string | null): string | null =>
    id ? (companies.data?.results.find((company) => company.id === id)?.name ?? null) : null;

  const sourceName = (id: string | null): string | null =>
    id ? (sources.data?.find((source) => source.id === id)?.name ?? null) : null;

  const member = (id: string | null) =>
    id ? people.find((person) => person.id === id) : undefined;

  const startAdding = (stageId: string): void => {
    setTitle('');
    setAdding(stageId);
  };

  // Arriving from the home screen's Criar menu opens the composer in the first stage — which needs
  // the pipeline loaded, so the intent waits for it rather than being dropped.
  useCreateIntent(() => stages[0] && startAdding(stages[0].id), stages.length > 0);

  return (
    <div className="view view--wide board-page">
      <header className="board-head">
        <div>
          <nav className="breadcrumb" aria-label={t('board.breadcrumb')}>
            <span>{t('nav.crm')}</span>
            <span aria-hidden="true">/</span>
            <span className="strong">{t('crm.pipeline')}</span>
          </nav>

          <h1>{t('crm.pipeline')}</h1>
          <p className="muted">{t('board.description')}</p>
        </div>

        <div className="cell-inline">
          <button
            type="button"
            className="button button--primary"
            disabled={stages.length === 0}
            onClick={() => startAdding(stages[0]!.id)}
          >
            + {t('board.newDeal')}
          </button>
        </div>
      </header>

      <div className="metrics metrics--icons">
        <Figure
          icon={<OpenValueIcon />}
          tone="neutral"
          label={t('board.open')}
          value={formatMoney(totals.openAmount)}
          hint={t('board.openHint').replace('{n}', String(totals.openCount))}
        />
        <Figure
          icon={<ForecastIcon />}
          tone="info"
          label={t('board.forecast')}
          value={formatMoney(Math.round(totals.forecast))}
          hint={t('board.forecastHint')}
        />
        <Figure
          icon={<WonIcon />}
          tone="positive"
          label={t('board.won')}
          value={formatMoney(totals.won)}
          hint={t('board.wonHint').replace('{n}', String(totals.wonCount))}
          strong
        />
        <Figure
          icon={<StackIcon />}
          tone="neutral"
          label={t('board.openDeals')}
          value={String(totals.openCount)}
          hint={t('board.stagesHint').replace('{n}', String(totals.openStages))}
        />
      </div>

      <form
        className="board-filters"
        onSubmit={(formEvent) => {
          formEvent.preventDefault();
          setApplied(search);
        }}
      >
        <select
          className="select"
          aria-label={t('crm.pipeline')}
          value={chosen ?? ''}
          onChange={(changeEvent) => setPipelineId(changeEvent.target.value)}
        >
          {(pipelines.data?.pipelines ?? []).map((pipeline: Pipeline) => (
            <option key={pipeline.id} value={pipeline.id}>
              {pipeline.name}
            </option>
          ))}
        </select>

        <label className="board-search">
          <SearchGlyph />
          <input
            value={search}
            placeholder={t('board.searchPlaceholder')}
            aria-label={t('board.searchPlaceholder')}
            onChange={(changeEvent) => setSearch(changeEvent.target.value)}
          />
        </label>

        <div className="board-filters__owner">
          <span className="muted small">{t('board.owner')}</span>
          <MemberSelect
            value={ownerId || null}
            placeholder={t('board.everyone')}
            onChange={(next) => setOwnerId(next ?? '')}
          />
        </div>

        <select
          className="select"
          aria-label={t('catalogues.tab.sources')}
          value={sourceId}
          onChange={(changeEvent) => setSourceId(changeEvent.target.value)}
        >
          <option value="">{t('board.allSources')}</option>
          {(sources.data ?? []).map((source) => (
            <option key={source.id} value={source.id}>
              {source.name}
            </option>
          ))}
        </select>

        <button type="submit" className="button button--quiet">
          <FilterGlyph />
          {t('filters.apply')}
        </button>

        {/* The same deals, two ways to read them. A board answers "where is everything"; a list
            answers "show me all of them", which a board with six columns cannot. */}
        <div className="segmented" role="group" aria-label={t('board.view')}>
          <button
            type="button"
            className={`segmented__option${view === 'KANBAN' ? ' is-active' : ''}`}
            aria-pressed={view === 'KANBAN'}
            onClick={() => setView('KANBAN')}
          >
            <KanbanGlyph />
            {t('board.kanban')}
          </button>
          <button
            type="button"
            className={`segmented__option${view === 'LIST' ? ' is-active' : ''}`}
            aria-pressed={view === 'LIST'}
            onClick={() => setView('LIST')}
          >
            <ListGlyph />
            {t('board.list')}
          </button>
        </div>
      </form>

      {error && (
        <p className="alert" role="alert">
          {error}
        </p>
      )}

      {pipelines.error ? (
        <ErrorState error={pipelines.error} onRetry={() => void pipelines.refetch()} />
      ) : board.error ? (
        <ErrorState error={board.error} onRetry={() => void board.refetch()} />
      ) : pipelines.isLoading || board.isFetching ? (
        <Loading />
      ) : columns.length === 0 ? (
        <EmptyState title={t('board.noPipeline')} description={t('board.noPipelineBody')} />
      ) : view === 'LIST' ? (
        <DealList
          columns={columns}
          companyName={companyName}
          sourceName={sourceName}
          member={member}
          onOpen={(card) => navigate(`/crm/negocios/${card.id}`)}
        />
      ) : (
        <>
          <div className="board">
            {columns.map((column) => (
              <section
                key={column.stage.id}
                className={[
                  'board__column',
                  column.stage.is_won ? 'board__column--won' : '',
                  column.stage.is_lost ? 'board__column--lost' : '',
                  over === column.stage.id ? 'board__column--over' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
                style={{ '--stage-color': stageColor(column.stage) } as React.CSSProperties}
                onDragOver={(dragEvent) => {
                  if (!dragging) return;

                  dragEvent.preventDefault();
                  setOver(column.stage.id);
                }}
                onDragLeave={() =>
                  setOver((current) => (current === column.stage.id ? null : current))
                }
                onDrop={(dropEvent) => {
                  dropEvent.preventDefault();
                  setOver(null);

                  if (dragging && dragging.fromStageId !== column.stage.id) {
                    move.mutate({ card: dragging.card, stageId: column.stage.id });
                  }

                  setDragging(null);
                }}
              >
                <header className="board__header">
                  <span className="board__title">
                    <span className="board__dot" aria-hidden="true" />
                    <h2>{column.stage.name}</h2>
                    <span className="board__count">{column.count}</span>
                  </span>

                  <StageMenu
                    stage={column.stage}
                    stages={stages}
                    onAdd={() => startAdding(column.stage.id)}
                  />
                </header>

                <p className="board__total">{formatMoney(column.amount_cents)}</p>

                <ul className="board__cards">
                  {column.cards.map((card) => (
                    <DealCard
                      key={card.id}
                      card={card}
                      column={column}
                      stages={stages}
                      companyName={companyName(card.company_id)}
                      sourceName={sourceName(card.source_id)}
                      owner={member(card.owner_id)}
                      dragging={dragging?.card.id === card.id}
                      onDragStart={() => setDragging({ card, fromStageId: column.stage.id })}
                      onDragEnd={() => {
                        setDragging(null);
                        setOver(null);
                      }}
                      onMove={(stageId) => move.mutate({ card, stageId })}
                      onRemove={() => remove.mutate(card)}
                    />
                  ))}
                </ul>

                {column.cards.length === 0 && adding !== column.stage.id && (
                  <div className="board__empty">
                    <EmptyTrayGlyph />
                    <span className="muted small">
                      {column.stage.is_lost
                        ? t('board.emptyLost')
                        : column.stage.is_won
                          ? t('board.emptyWon')
                          : t('board.emptyColumn')}
                    </span>
                  </div>
                )}

                {column.count > column.cards.length && (
                  <p className="muted small board__more">
                    {t('board.more').replace('{n}', String(column.count - column.cards.length))}
                  </p>
                )}

                {adding === column.stage.id ? (
                  <AddDeal
                    value={title}
                    pending={create.isPending}
                    onChange={setTitle}
                    onCancel={() => setAdding(null)}
                    onSubmit={() => create.mutate(column.stage.id)}
                  />
                ) : (
                  <button
                    type="button"
                    className="board__add"
                    onClick={() => startAdding(column.stage.id)}
                  >
                    + {t('board.addDeal')}
                  </button>
                )}
              </section>
            ))}
          </div>

          <p className="muted small board__hint">{t('board.dragHint')}</p>
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// The card
// ---------------------------------------------------------------------------

function DealCard({
  card,
  column,
  stages,
  companyName,
  sourceName,
  owner,
  dragging,
  onDragStart,
  onDragEnd,
  onMove,
  onRemove,
}: {
  card: BoardCard;
  column: BoardColumn;
  stages: BoardColumn['stage'][];
  companyName: string | null;
  sourceName: string | null;
  owner: { name: string; avatarUrl?: string | null } | undefined;
  dragging: boolean;
  onDragStart: () => void;
  onDragEnd: () => void;
  onMove: (stageId: string) => void;
  onRemove: () => void;
}): React.JSX.Element {
  const won = stages.find((stage) => stage.is_won);
  const lost = stages.find((stage) => stage.is_lost);

  return (
    <li
      className={`board__card${dragging ? ' is-dragging' : ''}`}
      draggable
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
    >
      <div className="board__card-top">
        <span className="board__grip" aria-hidden="true">
          <GripGlyph />
        </span>

        {column.stage.is_won && (
          <span className="board__won-badge" aria-label={t('catalogues.won')}>
            <WonBadgeGlyph />
          </span>
        )}

        <Menu label={t('board.cardMenu')}>
          {won && !column.stage.is_won && (
            <button type="button" onClick={() => onMove(won.id)}>
              {t('catalogues.won')}
            </button>
          )}

          {lost && !column.stage.is_lost && (
            <button type="button" onClick={() => onMove(lost.id)}>
              {t('catalogues.lost')}
            </button>
          )}

          {/*
            Moving without a mouse.
            Dragging is faster with one and impossible without, so the same move is here as a list
            of stages rather than as a select nobody can find.
          */}
          {stages
            .filter((stage) => stage.id !== column.stage.id && !stage.is_won && !stage.is_lost)
            .map((stage) => (
              <button key={stage.id} type="button" onClick={() => onMove(stage.id)}>
                {t('board.moveToStage').replace('{name}', stage.name)}
              </button>
            ))}

          <button type="button" className="is-danger" onClick={onRemove}>
            {t('catalogues.remove')}
          </button>
        </Menu>
      </div>

      <Link to={`/crm/negocios/${card.id}`} className="board__link">
        {card.title}
      </Link>

      {companyName && <span className="board__company">{companyName}</span>}

      <span className="board__amount">{formatMoney(Number(card.amount_cents))}</span>

      {sourceName && <span className="board__tag">{sourceName}</span>}

      <div className="board__foot">
        {owner ? (
          <Person size="xs" name={owner.name} url={owner.avatarUrl} />
        ) : (
          <span className="muted small">{t('crm.unassigned')}</span>
        )}

        {card.stage_changed_at && !column.stage.is_won && !column.stage.is_lost && (
          <span className="board__age">
            <ClockGlyph />
            {t('board.inStage').replace('{n}', String(daysSince(card.stage_changed_at)))}
          </span>
        )}

        {card.expected_close_date && (column.stage.is_won || column.stage.is_lost) && (
          <span className="muted small">{formatDate(card.expected_close_date)}</span>
        )}
      </div>
    </li>
  );
}

function StageMenu({
  stage,
  stages,
  onAdd,
}: {
  stage: BoardColumn['stage'];
  stages: BoardColumn['stage'][];
  onAdd: () => void;
}): React.JSX.Element {
  return (
    <Menu label={t('board.stageMenu')}>
      <button type="button" onClick={onAdd}>
        {t('board.addDeal')}
      </button>

      <Link to="/configuracoes/catalogos" role="menuitem">
        {t('board.editStages').replace('{name}', stage.name)}
      </Link>

      <span className="menu__note">
        {t('board.stageProbability').replace('{n}', String(stage.probability))}
        {stages.length > 0 ? '' : ''}
      </span>
    </Menu>
  );
}

/** The inline composer a column opens when somebody adds a deal to it. */
function AddDeal({
  value,
  pending,
  onChange,
  onCancel,
  onSubmit,
}: {
  value: string;
  pending: boolean;
  onChange: (next: string) => void;
  onCancel: () => void;
  onSubmit: () => void;
}): React.JSX.Element {
  const input = useRef<HTMLInputElement>(null);

  // Focused on open: somebody who clicked "adicionar" is about to type, and a box they have to click
  // again is a box that costs a click every single time.
  useEffect(() => input.current?.focus(), []);

  return (
    <form
      className="board__compose"
      onSubmit={(formEvent) => {
        formEvent.preventDefault();
        if (value.trim()) onSubmit();
      }}
    >
      <input
        ref={input}
        value={value}
        maxLength={200}
        placeholder={t('board.newDealPlaceholder')}
        aria-label={t('board.newDealPlaceholder')}
        onChange={(changeEvent) => onChange(changeEvent.target.value)}
        onKeyDown={(keyEvent) => {
          if (keyEvent.key === 'Escape') onCancel();
        }}
      />

      <div className="cell-inline">
        <button
          type="submit"
          className="button button--primary button--compact"
          disabled={value.trim() === '' || pending}
        >
          {pending ? t('work.creating') : t('common.save')}
        </button>

        <button type="button" className="button button--quiet button--compact" onClick={onCancel}>
          {t('confirm.cancel')}
        </button>
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------
// The same deals, as a list
// ---------------------------------------------------------------------------

/**
 * Every deal in the funnel, in one table.
 *
 * A board answers "where is everything"; a list answers "show me all of them, sorted", which six
 * columns of cards cannot. It is built from the same board response rather than a second query, so
 * the two views can never disagree about what is in the funnel.
 */
function DealList({
  columns,
  companyName,
  sourceName,
  member,
  onOpen,
}: {
  columns: BoardColumn[];
  companyName: (id: string | null) => string | null;
  sourceName: (id: string | null) => string | null;
  member: (id: string | null) => { name: string; avatarUrl?: string | null } | undefined;
  onOpen: (card: BoardCard) => void;
}): React.JSX.Element {
  const rows = useMemo(
    () =>
      columns.flatMap((column) =>
        column.cards.map((card) => ({ ...card, stageName: column.stage.name })),
      ),
    [columns],
  );

  const tableColumns: Column<(typeof rows)[number]>[] = [
    {
      key: 'title',
      header: t('board.deal'),
      text: (row) => `${row.title} ${companyName(row.company_id) ?? ''}`,
      render: (row) => (
        <span className="cell-stack">
          <span className="strong">{row.title}</span>
          <span className="muted small">{companyName(row.company_id) ?? '—'}</span>
        </span>
      ),
    },
    {
      key: 'stage',
      header: t('board.stage'),
      text: (row) => row.stageName,
      render: (row) => row.stageName,
    },
    {
      key: 'amount',
      header: t('board.amount'),
      align: 'end',
      text: (row) => String(row.amount_cents),
      render: (row) => <span className="strong">{formatMoney(Number(row.amount_cents))}</span>,
    },
    {
      key: 'owner',
      header: t('board.owner'),
      text: (row) => member(row.owner_id)?.name ?? '',
      render: (row) => {
        const owner = member(row.owner_id);

        return owner ? (
          <Person size="xs" name={owner.name} url={owner.avatarUrl} />
        ) : (
          <span className="muted small">{t('crm.unassigned')}</span>
        );
      },
    },
    {
      key: 'source',
      header: t('catalogues.tab.sources'),
      hidden: true,
      text: (row) => sourceName(row.source_id) ?? '',
      render: (row) => sourceName(row.source_id) ?? '—',
    },
    {
      key: 'age',
      header: t('board.age'),
      hidden: true,
      text: (row) => (row.stage_changed_at ? String(daysSince(row.stage_changed_at)) : ''),
      render: (row) =>
        row.stage_changed_at
          ? t('board.inStage').replace('{n}', String(daysSince(row.stage_changed_at)))
          : '—',
    },
    {
      key: 'close',
      header: t('board.expectedClose'),
      hidden: true,
      render: (row) => formatDate(row.expected_close_date),
    },
  ];

  return (
    <DataTable
      tableKey="deals"
      rows={rows}
      columns={tableColumns}
      rowKey={(row) => row.id}
      onRowClick={onOpen}
      empty={{ title: t('board.emptyColumn') }}
    />
  );
}

// ---------------------------------------------------------------------------
// Bits
// ---------------------------------------------------------------------------

function Figure({
  icon,
  tone,
  label,
  value,
  hint,
  strong,
}: {
  icon: React.ReactNode;
  tone: 'neutral' | 'info' | 'positive';
  label: string;
  value: string;
  hint: string;
  strong?: boolean;
}): React.JSX.Element {
  return (
    <div className="metric metric--icon">
      <span className={`metric__icon metric__icon--${tone}`} aria-hidden="true">
        {icon}
      </span>

      <span className="cell-stack">
        <span className="metric__label">{label}</span>
        <span className={`metric__value${strong ? ' metric__value--positive' : ''}`}>{value}</span>
        <span className="muted small">{hint}</span>
      </span>
    </div>
  );
}

/**
 * A stage's colour, from what the stage means rather than from a column somebody has to fill in.
 *
 * Won is green and lost is red because those are the two everybody reads first; the rest run from
 * grey to blue with the likelihood, so a board shows its own shape at a glance.
 */
function stageColor(stage: { probability: number; is_won: boolean; is_lost: boolean }): string {
  if (stage.is_won) return 'var(--color-up)';
  if (stage.is_lost) return 'var(--color-down)';

  return `color-mix(in srgb, var(--color-accent) ${Math.max(20, stage.probability)}%, #94a3b8)`;
}

/** Whole days, rounded down: "0 dias" is today, which is what somebody means by it. */
function daysSince(iso: string): number {
  return Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000));
}
